/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import cx from 'classnames';
import { Grid } from '../../Grid';
import { useLabels } from '@/hooks/useLabels';
import azCommonStyles from '../../../theme/globals.module.scss';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import styles from './styles.module.scss';
import { useYmmeDefaultData } from '@/features/ymme/utils/useYmmeDefaultData';
import { Button } from '@/components/Button';
import Image from '@/components/NextImage';
import { FITMENT_ICONS, CAR_FITMENT_STATUS_ICONS } from '@/constants/images/fitmentIcons';
import { getVehicleTypeCategory } from '@/utils/getVehicleTypeCategory';
const vehicleIconSrc = '/images/vehicle-new.svg';
const warningIconSrc = '/images/icon-fitment-warning-orange.svg';
const successIconSrc = '/images/icon-fitment-success-check.svg';
import { useId } from 'react';

type Props = {
  fitsVehicle?: boolean;
  hasVehicle?: boolean;
  vehicleDisplayName?: string;
  toggleShowYmme: (() => void) | null;
  isList?: boolean;
  customClass?: string;
  onClickSeeFittingParts?: () => void;
  makeOrModelName?: string | null | undefined;
  displayVehicleInformation?: boolean;
  displayGenericFitmentMessage?: boolean;
  customizedVehicleName?: string | undefined;
  isConfigurableSku?: boolean;
  variant?: 'shortened';
};

const labelMap = {
  label_change_vehicle: 'hyperlink_YMME_Body_ChangeVehicle',
  label_fits_your_vehicle: 'label_fits_your_vehicle',
  label_doesnt_fit_vehicle: 'label_doesnt_fit_vehicle',
  lblFitsA: 'label_ymm_fitsYour_prefix', //Fits a
  label_see_parts: 'label_fitment_button_see_parts',
  label_fitment_button_if_this_fits: 'label_fitment_button_if_this_fits',
  label_fitment_button_check: 'label_fitment_button_check',
  label_ymm_fitment_prefix: 'label_ymm_fitment_prefix',
  label_doesnt_fit_your: 'label_doesnt_fit_your',
  label_customized_for_your: 'label_customized_for_your',
  lblFitmentCheckVehicleAria: 'label_fitment_check_vehicle_aria',
  lblFitmentBadgeFitsYour: 'label_fitment_badge_fits_your', //Fits your
};

const FitmentButton = ({
  fitsVehicle,
  hasVehicle,
  vehicleDisplayName,
  toggleShowYmme,
  isList = true,
  variant,
  customClass,
  makeOrModelName,
  onClickSeeFittingParts,
  displayVehicleInformation,
  displayGenericFitmentMessage = false,
  customizedVehicleName,
  isConfigurableSku,
}: Props) => {
  const { data: ymmeData } = useYmmeDefaultData();
  const preferredVehicle = usePreferredVehicle();
  const labels = useLabels(labelMap);
  const defaultEngine = ymmeData?.defaultEngine;

  const effectiveVehicleTypeName =
    preferredVehicle?.vehicleTypeName ??
    preferredVehicle?.acesVehicleAttributes?.vehicleQuestionsNames?.vehicleTypeName;
  const vehicleTypeCategory = getVehicleTypeCategory(effectiveVehicleTypeName);
  const nonCarTypeIcon = vehicleTypeCategory !== 'car';
  const listModifier = isList ? styles['fitmentButton--list'] : styles['fitmentButton--grid'];
  let buttonClassName = cx(
    azCommonStyles['az-body-2-regular'],
    styles.vehicleFit,
    styles.fitmentButton,
    listModifier,
    styles[variant ?? ''],
  );

  let buttonImage = null;
  let nonCarFitmentIcon: string | null = null;
  let buttonText = null;
  let testId = null;
  const isShortenedVariant = variant === 'shortened';
  const uniqueId = useId();
  const mainSpanId = `fitment-button-text-main-span-${uniqueId}`;
  let ariaLabel: string | undefined = undefined;
  let isDoesNotFit = false;
  if (makeOrModelName && !hasVehicle && !defaultEngine) {
    buttonImage = (
      <Image src={warningIconSrc} alt="" width={10} height={10} className={styles.fitmentIcon} />
    );
    buttonText = (
      <>
        <span className={styles.underlined}>{labels.label_fitment_button_check}</span>{' '}
        {labels.label_ymm_fitment_prefix}
        <strong>{` ${makeOrModelName}`}</strong>
      </>
    );
    testId = 'fitment-button-no-vehicle-selected';
  } else if (!hasVehicle) {
    buttonImage = (
      <Image src={warningIconSrc} alt="" width={10} height={10} className={styles.fitmentIcon} />
    );
    buttonText = (
      <>
        <span className={styles.underlined}>{labels.label_fitment_button_check}</span>{' '}
        {labels.label_fitment_button_if_this_fits}
      </>
    );
    testId = 'fitment-button-no-vehicle';
    ariaLabel = labels.lblFitmentCheckVehicleAria;
  } else if (fitsVehicle && !isConfigurableSku) {
    if (nonCarTypeIcon) {
      nonCarFitmentIcon = FITMENT_ICONS[vehicleTypeCategory].FITS;
    } else {
      buttonImage = <Image src={successIconSrc} alt="" fill className={styles.fitmentIcon} />;
    }
    buttonClassName += ` ${styles.fitsVehicle}`;
    const selectedVehicle = vehicleDisplayName
      ? vehicleDisplayName
      : `${preferredVehicle?.year} ${preferredVehicle?.make} ${preferredVehicle?.model}`;
    buttonText =
      hasVehicle && !displayGenericFitmentMessage ? (
        <>
          <span className={styles.successText}>
            {`${isShortenedVariant ? labels.lblFitmentBadgeFitsYour : labels.lblFitsA} `}
          </span>
          <span className={styles.underlined}>{selectedVehicle}</span>
        </>
      ) : (
        <span className={styles.successText}>{labels.label_fits_your_vehicle}</span>
      );
    testId = 'fitment-button-fits-vehicle';
  } else if (isConfigurableSku) {
    buttonImage = <Image src={successIconSrc} alt="" fill className={styles.fitmentIcon} />;
    buttonClassName += ` ${styles.fitsVehicle}`;
    buttonText = (
      <>
        <span className={styles.successText}>{labels.label_customized_for_your}</span>{' '}
        <span className={styles.underlined}>{customizedVehicleName}</span>
      </>
    );
    testId = 'fitment-button-custom-sku-vehicle';
  } else {
    if (nonCarTypeIcon) {
      nonCarFitmentIcon = FITMENT_ICONS[vehicleTypeCategory].DOES_NOT_FIT;
    } else {
      buttonImage = (
        <Image
          src={CAR_FITMENT_STATUS_ICONS.DOES_NOT_FIT}
          fill
          alt=""
          className={styles.fitmentIcon}
        />
      );
    }
    isDoesNotFit = true;
    buttonClassName += ` ${styles.fitsVehicleError}`;
    buttonText = (
      <div className={styles.errorBtn}>
        {!displayVehicleInformation && (
          <div className={styles.errorText}>{labels.label_doesnt_fit_vehicle}</div>
        )}
        <div>
          {displayVehicleInformation ? (
            <>
              <span className={styles.errorText}>{labels.label_doesnt_fit_your}</span>{' '}
              <button
                type="button"
                className={cx(styles.inlineButton, styles.changeVehicle, styles.underlined)}
                onClick={toggleShowYmme ?? undefined}
              >
                {vehicleDisplayName}
              </button>
            </>
          ) : null}
          {!displayVehicleInformation && !displayGenericFitmentMessage ? (
            <button
              type="button"
              className={cx(styles.inlineButton, styles.changeVehicle, styles.underlined)}
              onClick={toggleShowYmme ?? undefined}
            >
              {labels.label_change_vehicle}
            </button>
          ) : null}
          {onClickSeeFittingParts && (
            <>
              <span className={styles.errorPipe}>|</span>
              <button
                type="button"
                className={cx(styles.inlineButton, styles.seeFittingPartsBtn, styles.underlined)}
                onClick={onClickSeeFittingParts}
              >
                {labels.label_see_parts}
              </button>
            </>
          )}
        </div>
      </div>
    );
    testId = 'fitment-button-doesnt-fit-vehicle';
  }

  const descChildren = (
    <>
      <div className={cx(styles.imgContainer, isShortenedVariant && styles.imgContainerShortened)}>
        {!isDoesNotFit && nonCarFitmentIcon ? (
          <Image src={nonCarFitmentIcon} alt="" width={32} height={23} />
        ) : (
          <>
            <Image
              src={vehicleIconSrc}
              alt=""
              width={isShortenedVariant ? 14 : 20}
              height={isShortenedVariant ? 10 : 14}
            />
            <div className={styles.imgSmall}>{buttonImage}</div>
          </>
        )}
      </div>
      <div
        className={cx(styles.descContainer, isShortenedVariant && styles.descContainerShortened)}
      >
        <span id={isDoesNotFit ? undefined : mainSpanId}>{buttonText}</span>
      </div>
    </>
  );

  return (
    <Grid item className={customClass}>
      {isDoesNotFit ? (
        <div
          className={cx(
            styles.imgContainer,
            isShortenedVariant && styles.imgContainerShortened,
            nonCarTypeIcon && styles.imgContainerNonCar,
            buttonClassName,
          )}
          data-testid={testId}
          role="group"
          aria-label={
            displayVehicleInformation
              ? `${labels.label_doesnt_fit_your} ${vehicleDisplayName}`
              : labels.label_doesnt_fit_vehicle
          }
        >
          {nonCarFitmentIcon ? (
            <Image src={nonCarFitmentIcon} alt="" width={32} height={23} />
          ) : (
            <>
              <Image
                src={CAR_FITMENT_STATUS_ICONS.vehicle}
                alt=""
                width={isShortenedVariant ? 14 : 20}
                height={isShortenedVariant ? 10 : 14}
              />
              <div className={styles.imgSmall}>{buttonImage}</div>
            </>
          )}
          {descChildren}
        </div>
      ) : (
        <Button
          variant="ghost"
          className={buttonClassName}
          onClick={toggleShowYmme ?? undefined}
          data-testid={testId}
          aria-labelledby={ariaLabel ? undefined : mainSpanId}
          aria-label={ariaLabel}
          disabled={!toggleShowYmme || isConfigurableSku}
        >
          {descChildren}
        </Button>
      )}
    </Grid>
  );
};

export default FitmentButton;
